import { formatPlanDuration } from './subscriptionDateUtil';
import {
  POS_DEMO_SLOT_COUNT,
  POS_DEMO_SLOT_LENGTH_MINUTES,
  POS_DEMO_SLOT_START_MINUTES,
} from '../constants/posConst';

// How the landing page (/) presents the plans an administrator configured on
// /admin -> Subscription Plans. Pure functions only — no Firebase — so the
// wording rules live in one place and the components just render them.

/**
 * The plans shown on the landing page's pricing toggle, in display order:
 * offered to clients, and not a free trial (a trial is granted, not bought).
 *
 * @param {object[]} plans - normalised, already sorted by subscribeSubscriptionPlans
 * @returns {object[]}
 */
export const landingPlans = (plans) => (plans || []).filter((plan) => plan.visibleToClients && !plan.isTrial);

/**
 * The free-trial plan whose length the landing page advertises ("7 days
 * free"). The first trial in display order; null when there is none.
 *
 * @param {object[]} plans
 * @returns {object|null}
 */
export const landingTrialPlan = (plans) => (plans || []).find((plan) => plan.isTrial) || null;

/**
 * Every option on the pricing toggle: the free trial first (it is where a
 * visitor starts), then the paid plans in display order.
 *
 * The trial does not need "visible to clients": that flag hides a plan from
 * a till's own renewal list, where a trial must never appear anyway.
 *
 * @param {object[]} plans - normalised, already sorted by subscribeSubscriptionPlans
 * @returns {object[]}
 */
export const pricingTogglePlans = (plans) => {
  const trial = landingTrialPlan(plans);
  return trial ? [trial, ...landingPlans(plans)] : landingPlans(plans);
};

/**
 * A trial's length for a badge, e.g. '7 days'. '' without a trial.
 *
 * @param {object|null} trial
 * @returns {string}
 */
export const trialLengthLabel = (trial) => (trial ? formatPlanDuration(trial.durationValue, trial.durationUnit) : '');

/**
 * What a plan saves against its compare-at price.
 *
 * @param {object} plan
 * @returns {{amount: number, percent: number}|null} null when there is no saving
 */
export const planSavings = (plan) => {
  const compare = Number(plan?.compareAtPrice) || 0;
  const price = Number(plan?.price) || 0;
  if (plan?.isCustom || compare <= price || compare <= 0) return null;
  return { amount: compare - price, percent: Math.round((1 - price / compare) * 100) };
};

/**
 * The plan with the biggest percentage saving — quoted in the hero ("Save 33%
 * yearly") and the pricing heading ("save up to 33%").
 *
 * @param {object[]} plans - landing plans
 * @returns {{plan: object, percent: number}|null}
 */
export const bestSavingsPlan = (plans) => (plans || []).reduce((best, plan) => {
  const saving = planSavings(plan);
  if (!saving || (best && best.percent >= saving.percent)) return best;
  return { plan, percent: saving.percent };
}, null);

/**
 * The first plan with a price — the "Then ₱199/month" figure in the hero.
 *
 * @param {object[]} plans - landing plans
 * @returns {object|null}
 */
export const entryPlan = (plans) => (plans || []).find((plan) => !plan.isCustom) || null;

/**
 * How often the price is charged, as the pricing card prints it beside the
 * amount: 'per month', 'every 6 months', 'per year', or 'for 7 days' on a trial.
 *
 * @param {object} plan
 * @returns {string}
 */
export const planPeriodLabel = (plan) => {
  if (plan?.isCustom) return 'custom pricing';
  if (plan?.isTrial) return `for ${formatPlanDuration(plan.durationValue, plan.durationUnit)}`;
  const value = Math.max(1, Math.floor(Number(plan?.durationValue) || 1));
  if (plan?.durationUnit === 'months') {
    if (value === 1) return 'per month';
    if (value === 12) return 'per year';
    return `every ${value} months`;
  }
  return value === 1 ? 'per day' : `every ${value} days`;
};

/**
 * The short suffix after the hero's price: '/month', '/year', '/6 months'.
 *
 * @param {object} plan
 * @returns {string}
 */
export const planShortPeriod = (plan) => {
  const value = Math.max(1, Math.floor(Number(plan?.durationValue) || 1));
  if (plan?.durationUnit === 'months') {
    if (value === 1) return '/month';
    if (value === 12) return '/year';
    return `/${value} months`;
  }
  return value === 1 ? '/day' : `/${value} days`;
};

/**
 * The billing line in the checkout summary: 'Billed monthly', 'Billed every 6
 * months', 'Billed yearly', 'Custom quote', or the free-trial line.
 *
 * @param {object} plan
 * @returns {string}
 */
export const planBillingLabel = (plan) => {
  if (plan?.isCustom) return 'Custom quote';
  if (plan?.isTrial) return 'Free trial — no payment needed';
  const value = Math.max(1, Math.floor(Number(plan?.durationValue) || 1));
  if (plan?.durationUnit === 'months') {
    if (value === 1) return 'Billed monthly';
    if (value === 12) return 'Billed yearly';
    return `Billed every ${value} months`;
  }
  return value === 1 ? 'Billed daily' : `Billed every ${value} days`;
};

/**
 * The plan's length in the checkout summary: '1 month', '6 months', '12 months'.
 *
 * @param {object} plan
 * @returns {string}
 */
export const planSpanLabel = (plan) => (plan?.isCustom
  ? 'Tailored to your branches'
  : formatPlanDuration(plan?.durationValue, plan?.durationUnit));

/**
 * A time of day, e.g. '6:30 PM', from minutes after midnight. Wraps past
 * midnight, so 1470 (24:30) reads '12:30 AM'.
 *
 * @param {number} minutes
 * @returns {string}
 */
export const formatClockMinutes = (minutes) => {
  const h24 = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const suffix = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${m < 10 ? '0' : ''}${m} ${suffix}`;
};

/**
 * The demo-call slots offered for any day, in order.
 *
 * @returns {Array<{start: number, label: string, nextDay: boolean}>}
 */
export const buildDemoSlots = () => Array.from({ length: POS_DEMO_SLOT_COUNT }, (_, index) => {
  const start = POS_DEMO_SLOT_START_MINUTES + index * POS_DEMO_SLOT_LENGTH_MINUTES;
  return {
    start,
    label: `${formatClockMinutes(start)} – ${formatClockMinutes(start + POS_DEMO_SLOT_LENGTH_MINUTES)}`,
    nextDay: start >= 24 * 60,
  };
});

/**
 * One month of the booking calendar, Monday first: the number of blank cells
 * before the 1st, and every day of the month.
 *
 * @param {number} year
 * @param {number} month - 0-based
 * @returns {{lead: number, days: Date[]}}
 */
export const buildCalendarMonth = (year, month) => {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  return { lead, days: Array.from({ length: count }, (_, index) => new Date(year, month, index + 1)) };
};

/**
 * A local date as 'YYYY-MM-DD'.
 *
 * @param {Date} date
 * @returns {string}
 */
export const toDateString = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * A date the way the confirmation reads it: 'Thursday, October 1, 2026'.
 *
 * @param {Date} date
 * @returns {string}
 */
export const formatLongDate = (date) => date.toLocaleDateString('en-US', {
  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
});
